// S5 보스 분노·광폭(정본 docs/STAGE-WORLD-PRD.md 3.3): 스테이지 판(extras)의 n-5 중간보스는 체력 30% 이하 "분노",
// n-10 지역보스는 체력 25% 이하 "광폭"(페이즈 3)에 한 번 들어간다. 자유 입장 보스(extras = false)와 일반판(n-1~4, 보스 없음)에는 없다.
// 순수 규칙(DOM·three·GameSim 없음) — game/boss.ts(행동), ui/hud.ts(체력바 눈금·띠), ui/stageMapModel.ts(입장 카드), tools/sim/combat.ts(생존 시뮬)가 같이 쓴다.
// 공정성 원칙: 경고(텔레그래프) 시간은 어느 단계에서도 줄이지 않는다. 분노는 공격 사이 간격만 줄이고, 광폭의 두 번째 패턴은 원래 경고 시간 + 판정 간격 뒤에 터진다.
import { BOSS, BOSS_EMBER, BOSS_TIMING, HAZARD } from '../shared/config';
import { BOSS_EXTRA, type BossArchetype, type BossDef } from '../shared/content/bosses';

/** BossStateView.rage 값: 0 없음, 1 분노(중간보스), 2 광폭(지역보스, 페이즈 3) */
export const BossRage = { None: 0, Rage: 1, Frenzy: 2 } as const;
export type BossRageId = (typeof BossRage)[keyof typeof BossRage];

/** Ev.BossPattern a 번호(boss.ts BossPatternId와 같은 공간, 0~9 다음): 분노 진입 10, 광폭 진입 11, 광폭 두 번째 패턴 시작 12 */
export const RAGE_EVENT = { rage: 10, frenzy: 11, echo: 12 } as const;

/** 분노: 체력 비율 at 이하, 공격 빈도 ×attackRate(경고 시간 그대로, 공격 사이 간격만 줄인다), 추가 패턴 차례 세 번에 한 번 → 두 번에 한 번 */
export const RAGE = { at: 0.3, attackRate: 1.2 } as const;

/**
 * 광폭(페이즈 3): 체력 비율 at 이하. 공격마다 두 번째 패턴(메아리)을 같이 깐다(동시 2개).
 * 두 번째 패턴의 경고 길이 = max(첫 패턴 경고, 자기 원래 경고) + gap — 원래 경고보다 짧아지지 않고, 판정 순간이 첫 패턴과 gap 이상 떨어진다
 * (R2 판정 간격 규칙 HAZARD.minResolveGap과 같은 값, 회피 쿨다운 0.6초보다 길다).
 * cooldownMult: 두 패턴을 같이 쓰는 대신 공격 사이 간격을 늘린다(생존 시뮬 표준 손실 ≤ 60%, tools/sim/combat.ts).
 */
export const FRENZY = { at: 0.25, gap: HAZARD.minResolveGap, cooldownMult: 1.8 } as const;

/** 이 보스 판의 분노 종류(자유 입장·추가 패턴 없는 판은 없음) */
export function rageKindOf(def: BossDef, extras: boolean): BossRageId {
  if (!extras) return BossRage.None;
  return def.tier === 'mid' ? BossRage.Rage : BossRage.Frenzy;
}
/** 분노·광폭 체력 문턱(최대 체력 비율, 0 = 없음) */
export function rageThreshold(def: BossDef, extras: boolean): number {
  const k = rageKindOf(def, extras);
  return k === BossRage.Rage ? RAGE.at : k === BossRage.Frenzy ? FRENZY.at : 0;
}

/** 원형 패턴 평균 경고 길이(틱): 근접형 = 내려찍기·휩쓸기, 시전형 = 고리·운석 3연속·균열 */
export function avgWindup(arch: BossArchetype): number {
  if (arch === 'ember') return (BOSS_EMBER.novaWindup + BOSS_EMBER.meteorWindup + (BOSS_EMBER.meteorCount - 1) * BOSS_EMBER.meteorGap + BOSS_EMBER.fissureWindup) / 3;
  return (BOSS_TIMING.slamWindup + BOSS_TIMING.sweepWindup) / 2;
}
/** 원형 재사용 대기(틱) */
export function baseCooldown(arch: BossArchetype): number {
  return arch === 'ember' ? BOSS_EMBER.attackCooldown : BOSS_TIMING.attackCooldown;
}
/**
 * 분노 재사용 대기(틱): 한 주기(경고 + 대기)가 1/attackRate가 되게 대기만 줄인다. 근접형 96 → 69, 시전형 84 → 53.
 * 경고 시간은 그대로라 반응할 시간은 같고, 쉬는 틈만 짧아진다.
 */
export function rageCooldown(arch: BossArchetype): number {
  const w = avgWindup(arch), c = baseCooldown(arch);
  return Math.max(1, Math.round((w + c) / RAGE.attackRate - w));
}
/** 광폭 재사용 대기(틱) */
export function frenzyCooldown(arch: BossArchetype, mult = 1): number {
  return Math.round(baseCooldown(arch) * mult * FRENZY.cooldownMult);
}

/** 광폭 두 번째 패턴 종류: mark = 영웅 자리 원, ring = 보스 둘레 큰 원(근접형), lines = 보스에서 뻗는 균열 3줄(시전형) */
export type EchoKind = 'mark' | 'ring' | 'lines';
/**
 * 첫 패턴(Ev.BossPattern 번호)에 겹칠 두 번째 패턴. 같은 자리를 두 번 노리지 않게 고른다:
 * 근접형 — 내려찍기(영웅 자리 원)에는 둘레 지진 원, 그 밖(휩쓸기·돌진·지진·물기)에는 영웅 자리 원.
 * 시전형 — 영웅 자리를 노리는 운석·포자에는 균열 3줄, 그 밖(고리·균열·십자)에는 영웅 자리 원.
 */
export function echoFor(arch: BossArchetype, primary: number): EchoKind {
  if (arch === 'ember') return primary === 2 || primary === 8 ? 'lines' : 'mark';
  return primary === 0 ? 'ring' : 'mark';
}
/** 두 번째 패턴 원래 경고 길이(틱) */
export function echoBaseWindup(arch: BossArchetype, kind: EchoKind): number {
  if (kind === 'ring') return BOSS_EXTRA.quake.windup;
  if (kind === 'lines') return BOSS_EMBER.fissureWindup;
  return arch === 'ember' ? BOSS_EMBER.meteorWindup : BOSS_TIMING.slamWindup;
}
/** 두 번째 패턴 원형 피해(배율 전) */
export function echoBaseDamage(arch: BossArchetype, kind: EchoKind): number {
  if (kind === 'ring') return BOSS.damage * BOSS_EXTRA.quake.dmg;
  if (kind === 'lines') return BOSS_EMBER.fissureDamage;
  return arch === 'ember' ? BOSS_EMBER.meteorDamage : BOSS.damage;
}
/** 두 번째 패턴 경고 길이(틱): 원래 경고와 첫 패턴 경고 중 긴 쪽 + gap(절대 원래보다 짧지 않다) */
export function echoWindup(arch: BossArchetype, kind: EchoKind, primaryWindup: number): number {
  return Math.max(primaryWindup, echoBaseWindup(arch, kind)) + FRENZY.gap;
}

/** 표시 문구(체력바 띠·입장 카드) */
export const RAGE_TEXT: Readonly<Record<1 | 2, { readonly name: string; readonly head: string; readonly tip: string; readonly card: string }>> = {
  1: { name: '분노', head: '분노!', tip: '공격이 빨라진다', card: `체력 ${Math.round(RAGE.at * 100)}% 이하 분노 — 공격 속도 +${Math.round((RAGE.attackRate - 1) * 100)}%, 추가 패턴 자주` },
  2: { name: '광폭', head: '광폭!', tip: '패턴 두 개가 함께 온다', card: `체력 ${Math.round(FRENZY.at * 100)}% 이하 광폭 — 패턴 동시 2개(경고 시간 그대로)` },
};

/** 체력바 눈금 표시(순수): 보스 변형의 등급(중간 → 분노, 지역 → 광폭)과 문턱. 문턱이 0이면 null */
export interface RageMark { readonly kind: 1 | 2; readonly pct: number; readonly name: string; readonly color: string; readonly edge: string }
export function rageMark(tier: 'mid' | 'final', rageAt: number): RageMark | null {
  if (!(rageAt > 0 && rageAt < 1)) return null;
  const kind: 1 | 2 = tier === 'mid' ? 1 : 2;
  return { kind, pct: Math.round(rageAt * 1000) / 10, name: RAGE_TEXT[kind].name, color: kind === 1 ? '#ff4a1c' : '#a040ff', edge: kind === 1 ? '#ffb040' : '#ff5ae0' };
}
/** 전환 알림 띠를 보이는 시간(ms) */
export const RAGE_BAND_MS = 3200;
